import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Heart, Ticket, Crown } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Avatar } from "@/components/PostCard";
import VerifiedBadge from "@/components/VerifiedBadge";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const RANK = ["text-amber-400", "text-zinc-300", "text-orange-400"];

export default function TopEarners({ title = "Top Earners" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard/top-earners").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="nu-card p-5" data-testid="top-earners">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-5 h-5 text-amber-400" />
        <h2 className="font-bold text-lg">{title}</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Highest-earning artists this week · tips + party tickets</p>
      {loading ? (
        <p className="text-zinc-500 text-sm py-4 font-mono-nu">loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-zinc-500 text-sm py-4">No earnings this week yet. Tip an artist to kick it off! 💸</p>
      ) : (
        <div className="space-y-1" data-testid="top-earners-list">
          {rows.map((r, i) => (
            <motion.div
              key={r.artist.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link to={`/artist/${r.artist.id}`} data-testid={`top-earner-${i}`}
                className="flex items-center gap-3 py-2.5 px-2 rounded-xl hover:bg-white/5 transition-colors group">
                <span className={`w-6 text-center font-display font-black ${RANK[i] || "text-zinc-600"} flex items-center justify-center`}>
                  {i === 0 ? <Crown className="w-5 h-5" /> : i + 1}
                </span>
                <Avatar artist={r.artist} size={40} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate flex items-center gap-1 group-hover:text-emerald-400 transition-colors">
                    {r.artist.artist_name}<VerifiedBadge show={r.artist.is_subscribed} size={14} />
                  </p>
                  <p className="text-xs text-zinc-500 flex items-center gap-3 font-mono-nu">
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-400" /> {r.tips}</span>
                    <span className="flex items-center gap-1"><Ticket className="w-3 h-3 text-violet-400" /> {r.tickets}</span>
                  </p>
                </div>
                <span className="font-mono-nu text-emerald-400 font-bold shrink-0">{money(r.earned_cents)}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
